/**
 * Header component with global filter bar, notifications, and user menu.
 */

import { useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowLeft,
  Bell,
  ChevronDown,
  LogOut,
  Menu,
  Moon,
  Search,
  Settings,
  Sun,
  User,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAuthContext } from '@/hooks/AuthContext'
import { useThemeContext, type Theme } from '@/contexts/ThemeContext'
import { useUserPreferencesStore } from '@/stores/userPreferencesStore'
import { useNavigationStore } from '@/stores/navigationStore'
import { useStudyStore } from '@/stores/studyStore'
import { useNotificationStore } from '@/stores/notificationStore'
import { NotificationPanel } from './NotificationPanel'
import { GlobalFilterBar } from './GlobalFilterBar'
import { StudySelector } from './StudySelector'
import { getBranding } from '@/config/branding'
import { isOnStudySubdomain, navigateToMainApp } from '@/lib/subdomain'
import { useCommandMenuStore } from './CommandMenu'
import { useState } from 'react'

export function Header() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthContext()
  const { theme } = useThemeContext()
  const updatePreferences = useUserPreferencesStore((s) => s.updatePreferences)
  const { setSidebarOpen } = useNavigationStore()
  const currentStudy = useStudyStore((state) => state.currentStudy)
  const unreadCount = useNotificationStore((state) => state.unreadCount)
  const openCommandMenu = useCommandMenuStore((state) => state.setOpen)
  const [showNotifications, setShowNotifications] = useState(false)

  const branding = getBranding()

  // Determine if we're on a study-specific route where filters are relevant
  // Filters should show on: subdomain routes OR /study/:studyId/* routes
  // Filters should NOT show on: /dashboard, /studies, /admin/*, /account
  const onSubdomain = isOnStudySubdomain()
  const isStudyRoute = location.pathname.startsWith('/study/')
  const showGlobalFilters = currentStudy && (onSubdomain || isStudyRoute)

  const toggleTheme = () => {
    let next: Theme
    if (theme === 'dark') {
      next = 'light'
    } else if (theme === 'light') {
      next = 'system'
    } else {
      next = 'dark'
    }
    updatePreferences({ theme: next }).catch(() => {})
  }

  const getThemeIcon = () => {
    if (theme === 'dark') return <Moon className="h-5 w-5" />
    if (theme === 'light') return <Sun className="h-5 w-5" />
    return <Sun className="h-5 w-5 opacity-50" /> // System
  }

  const getThemeLabel = () => {
    if (theme === 'dark') return 'Dark mode'
    if (theme === 'light') return 'Light mode'
    return 'System theme'
  }

  const getInitials = (name: string | undefined) => {
    if (!name) return 'U'
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 items-center gap-4 px-4 lg:px-6">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle menu</span>
        </Button>

        {/* Logo (mobile only) */}
        <div className="flex items-center gap-2 lg:hidden">
          <span className="font-bold text-lg">{branding.appShortName}</span>
        </div>

        {/* Back to main app + Study selector (only on study-specific routes) */}
        {(onSubdomain || isStudyRoute) && (
          <div className="hidden md:flex items-center gap-2">
            {onSubdomain && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigateToMainApp('/studies')}
                  >
                    <ArrowLeft className="h-5 w-5" />
                    <span className="sr-only">Back to All Studies</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Back to All Studies</TooltipContent>
              </Tooltip>
            )}
            <StudySelector />
          </div>
        )}

        {/* Global filter bar (only on study-specific routes) */}
        {showGlobalFilters && (
          <div className="hidden lg:flex flex-1 items-center justify-center">
            <GlobalFilterBar />
          </div>
        )}

        {/* Spacer - pushes right side actions to the right */}
        {/* On mobile: always show spacer. On desktop: show spacer when no filter bar */}
        <div className={showGlobalFilters ? "flex-1 lg:hidden" : "flex-1"} />

        {/* Right side actions */}
        <div className="flex items-center gap-2">
          {/* Search - opens command menu (Ctrl+K) */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="hidden md:flex" onClick={() => openCommandMenu(true)}>
                <Search className="h-5 w-5" />
                <span className="sr-only">Search (Ctrl+K)</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Search (Ctrl+K)</TooltipContent>
          </Tooltip>

          {/* Theme toggle */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={toggleTheme}>
                {getThemeIcon()}
                <span className="sr-only">{getThemeLabel()}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{getThemeLabel()}</TooltipContent>
          </Tooltip>

          {/* Notifications */}
          <DropdownMenu open={showNotifications} onOpenChange={setShowNotifications}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <Badge
                    variant="destructive"
                    className="absolute -right-1 -top-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
                <span className="sr-only">Notifications</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 p-0">
              <NotificationPanel onClose={() => setShowNotifications(false)} />
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 gap-2 px-2">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {getInitials(user?.fullName)}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden md:inline-flex text-sm font-medium">
                  {user?.fullName?.split(' ')[0]}
                </span>
                <ChevronDown className="h-4 w-4 hidden md:inline-flex text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {user?.fullName}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/account')}>
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/account/preferences')}>
                <Settings className="mr-2 h-4 w-4" />
                Preferences
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile: Study selector and filter bar (only on study-specific routes) */}
      {(onSubdomain || isStudyRoute) && (
        <div className="flex flex-col gap-2 border-t px-4 py-2 md:hidden">
          <div className="flex items-center gap-2">
            {onSubdomain && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigateToMainApp('/studies')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <div className="flex-1">
              <StudySelector />
            </div>
          </div>
          {showGlobalFilters && <GlobalFilterBar />}
        </div>
      )}
    </header>
  )
}
